<template>
  <v-card>
    <v-card-title class="pb-0 mb-0">
      <div class="card-title">
        <span class="bold table-card-title">
          <slot name="title">Meus destaques</slot>
        </span>
        <div class="status-search-group">
          <mf-status-filter v-if="itemsFilter.length > 1" :items="itemsFilter" :value="itemsFilter.map((i) => i.value)" @input="getFilterResult" />
          <headers-data-table-settings :headers.sync="headers" :default-headers="getDefaultHeadersOptions()" />
          <div class="min-width-345">
            <mf-search v-model="search" class="pt-0 mt-0" hide-details placeholder="Buscar destaques" />
          </div>
        </div>
      </div>
    </v-card-title>
    <v-card-text class="pa-0">
      <paginated-data-table
        ref="table"
        class="history-striped"
        :headers="computedHeaders"
        :search="search"
        :query="query"
        :query-variables="queryVariables"
        :custom-items-per-page="franchisee ? 5 : 20"
        :items-per-page-options="[5, 10, 15, 20]"
        :graphql-uri="$microservicesUrls['fedGateway']"
        :default-table-sort-by="defaultTableSortBy"
        :default-table-sort-desc="defaultTableSortDesc"
        :reset-to-default-sort-when-empty="resetToDefaultSortWhenEmpty"
        query-name="highlightedOffers"
      >
        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-icon fill :size="16" :color="getStatusLabel(item.status).color" :icon="'fiber_manual_record'" />
              {{ getStatusLabel(item.status).text }}
            </div>
          </div>
        </template>

        <template v-slot:item.title="{ item }" style="max-width: 300px">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <mf-tooltip v-model="showTooltipTitle[item.id]" max-width="250" top content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <router-link v-if="!franchisee" class="link" :to="getEditRoute(item.id, item.status)" v-on="on">
                    {{ item.title | truncate({ length: nameTruncate }) }}
                  </router-link>
                  <span v-else v-on="on">{{ item.title | truncate({ length: nameTruncate }) }}</span>
                </template>
                <span>{{ item.title }}</span>
              </mf-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.description="{ item }" style="max-width: 300px">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip v-model="showTooltipDescription[item.id]" top max-width="250" content-class="truncated-tooltip">
                <template v-slot:activator="{ on }">
                  <span v-on="on">{{ item.description ? item.description || truncate({ length: 150 }) : '-' }}</span>
                </template>
                <span>{{ item.description ? item.description : '-' }}</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.type="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <span v-if="item.type === 'DEFAULT'">Padrão</span>
              <span v-else-if="item.type === 'EXPANDED'">Expandido</span>
              <span v-else>-</span>
            </div>
          </div>
        </template>

        <template v-slot:item.units="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" style="cursor: auto" v-on="on">
                    {{ getUnits(item, true) }}
                  </span>
                </template>
                <span v-for="(unit, index) in item.units" :key="index" style="display: block">
                  {{ unit.name }}
                </span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.initialDate="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <div v-if="showTooltip(item.status)">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-if="item.initialDate && item.finalDate" v-on="on"
                      >{{ DateTime.fromISO(item.initialDate).toFormat('dd/MM/yyyy') }} - {{ DateTime.fromISO(item.finalDate).toFormat('dd/MM/yyyy') }}</span
                    >
                    <span v-else v-on="on"> - </span>
                  </template>
                  <span>{{ getHighlightDuration(item) }}</span>
                </v-tooltip>
              </div>
              <div v-else>
                <span v-if="item.initialDate && item.finalDate">
                  {{ DateTime.fromISO(item.initialDate).toFormat('dd/MM/yyyy') }} - {{ DateTime.fromISO(item.finalDate).toFormat('dd/MM/yyyy') }}
                </span>
                <span v-else> - </span>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.updatedAt="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <span>
                {{ DateTime.fromISO(item.updatedAt).toFormat('dd/MM/yyyy') }}
              </span>
            </div>
          </div>
        </template>

        <template v-slot:item.menu="{ item }">
          <div class="d-flex align-center colorful-border" :style="{ borderColor: getStatusLabel(item.status).color }">
            <div class="py-0 px-4 d-flex align-center">
              <div v-if="!franchisee">
                <v-menu class="campaigns-table-actions" bottom origin="center center" transition="scale-transition">
                  <template v-slot:activator="{ on }">
                    <mf-button color="cinza-0" icon="more_vert" type="icon" v-on:click.native="on.click" />
                  </template>
                  <v-list>
                    <v-list-item @click="redirectToEdit(item.id)">
                      <v-list-item-title>{{ item.status === 'FINISHED' ? 'Visualizar' : 'Editar' }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'SCHEDULED'" @click="unscheduleHighlight(item.id, item.title)">
                      <v-list-item-title>Suspender</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'DRAFT'" data-cy="delete-highlight" @click="deleteHighlight(item.id, item.title)">
                      <v-list-item-title>Deletar</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="cloneHighlight(item.id, item.title)">
                      <v-list-item-title>Duplicar</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.status === 'ACTIVE'" @click="finishHighlight(item.id, item.title)">
                      <v-list-item-title>Finalizar</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="openLogsModal(item.id)">
                      <v-list-item-title>Logs</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="hasResults(item.status)" @click="redirectToResults(item.id)">
                      <v-list-item-title>Resultados</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </template>
      </paginated-data-table>
    </v-card-text>
    <logs-modal v-model="logsDialog" tags="HIGHLIGHTED_OFFER" logTitle="Destaques" :idItem="highlightId" />
  </v-card>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import { QUERY_GET_HIGHLIGHTED_OFFERS } from '../../../graphql'
import { DateTime } from 'luxon'
import LogsModal from '@/components/logs-modal/LogsModal.vue'
import { getChannelsPermissionsStatus, checkIfSomeChannelIsAllowed } from '@/modules/campanhas/gestao-ofertas-v2/helpers/channels'
import { getUnits, getStatusLabel } from '@/config/helpers.js'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'
import { highlightWarnings } from '../../../mixins/highlight-warnings'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import cloneDeep from 'lodash/cloneDeep'

export default {
  name: 'VoucherTable',
  components: {
    PaginatedDataTable,
    HeadersDataTableSettings,
    LogsModal,
  },
  mixins: [highlightWarnings],
  props: {
    franchisee: {
      type: Boolean,
      default: false,
    },
    units: {
      type: Array,
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
    partnerUnits: {
      type: Array,
    },
    products: {
      type: Array,
      default: () => [],
    },
    itemsFilter: {
      type: Array,
      default: () => [],
    },
    defaultTableSortBy: {
      type: [String, Array],
      default: '',
    },
    defaultTableSortDesc: {
      type: [Boolean, Array],
      default: false,
    },
    resetToDefaultSortWhenEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    highlightId: '',
    logsDialog: false,
    showTooltipTitle: {},
    showTooltipDescription: {},
    DateTime: DateTime,
    headers: [],
    statusToSearch: [],
  }),
  computed: {
    query() {
      return QUERY_GET_HIGHLIGHTED_OFFERS
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          width: header.width,
          sortable: header.hasOwnProperty('sortable') ? header.sortable : true,
        }
      })
    },
    queryVariables() {
      let queryVariables = {
        status: this.statusToSearch,
        franchise: this.franchisee,
        ...(this.units.length
          ? {
              units: this.units,
              units_configuration: this.unitsConfiguration,
              partner_units: this.partnerUnits,
            }
          : {}),
      }
      if (this.franchisee) {
        const draftPosition = queryVariables.status.indexOf('DRAFT')
        if (draftPosition > -1) queryVariables.status.splice(draftPosition, 1)
      }
      return queryVariables
    },
    defaultHeaders() {
      return [
        {
          text: 'Status',
          value: 'status',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Título',
          value: 'title',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Descrição',
          value: 'description',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Lojas',
          value: 'units',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Modelo',
          value: 'type',
          width: '7.5%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Vigência',
          value: 'initialDate',
          width: '17%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Última atualização',
          value: 'updatedAt',
          width: '17%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: '',
          value: 'menu',
          width: '5%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
      ]
    },
    isFranchise() {
      if (!this.$getLoggedUser().is_franchise) return false
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },
    isSomeChannelAllowed() {
      const channelsPermissionsStatus = getChannelsPermissionsStatus(this.$permissions, this.$getAllowedModules)
      return checkIfSomeChannelIsAllowed(channelsPermissionsStatus)
    },
    nameTruncate() {
      return this.industry ? 62 : 18
    },
  },
  beforeMount() {
    this.statusToSearch = this.itemsFilter.map((item) => item.value)
  },
  async mounted() {
    if (this.headers?.length <= 0) {
      this.headers = cloneDeep = this.defaultHeaders
    }
  },
  methods: {
    getStatusLabel,
    getUnits,
    getFilterResult(value) {
      this.statusToSearch = value.length ? value : this.itemsFilter.map((item) => item.value)
    },
    openLogsModal(id) {
      this.highlightId = id
      this.logsDialog = true
    },
    getEditRoute(id, status) {
      return status === 'FINISHED'
        ? { name: 'destaques-resultados', params: { id }, query: { franchise: this.franchise } }
        : { name: 'highlight-edit', params: { id } }
    },
    redirectToEdit(id) {
      const route = this.getEditRoute(id)
      this.$router.push(route).catch(() => {})
    },
    getResultsRoute(id) {
      return { name: 'destaques-resultados', params: { id }, query: { franchise: this.franchise } }
    },
    redirectToResults(id) {
      const route = this.getResultsRoute(id, this.franchise)
      this.$router.push(route).catch(() => {})
    },
    redirectToConsumedSalesSession(id, franchise) {
      const routeName = 'redirectToConsumedSalesSession'
      this.$router.push({ name: routeName, params: { id: id }, query: { franchise: franchise } }).catch(() => {})
    },
    async generateModalInfos(name, model) {
      const parsedName = name ? name.trim().bold() : ''
      const title = {
        clone: 'Duplicar destaque',
        delete: 'Remover destaque',
        unschedule: 'Suspender destaque',
        finish: 'Finalizar destaque',
      }
      const message = {
        clone: `Tem certeza que deseja duplicar o destaque ${parsedName}?`,
        delete: `Tem certeza que deseja excluir o destaque ${parsedName}?`,
        unschedule: `O destaque ${parsedName} será movido para o status de Rascunho`,
        finish: `O destaque ${parsedName} será movido para o status concluído`,
      }
      const confirmText = {
        clone: 'Duplicar',
        delete: 'Remover',
        unschedule: 'Ok',
        finish: 'Ok',
      }
      try {
        await this.$confirm({
          title: title[model],
          message: message[model],
          confirmText: confirmText[model],
          cancelText: 'Cancelar',
        })
        return true
      } catch (error) {
        return false
      }
    },
    async cloneHighlight(id, name) {
      const confirmed = await this.generateModalInfos(name, 'clone')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$store
        .dispatch('mutateHighlight', { id, action: 'clone' })
        .then((response) => {
          const id = response.data.cloneHighlightedOffer.id
          this.$router.push({ name: 'highlight-edit', params: { id } }).catch(() => {})
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.clone'))
        })
    },
    async deleteHighlight(id, name) {
      const confirmed = await this.generateModalInfos(name, 'delete')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$store
        .dispatch('mutateHighlight', { id, action: 'delete' })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.delete'))
        })
      this.$refs.table.paginationLoading = false
    },
    async unscheduleHighlight(id, name) {
      const confirmed = await this.generateModalInfos(name, 'unschedule')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$store
        .dispatch('mutateHighlight', { id, action: 'unschedule' })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch(() => {
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },
    async finishHighlight(id, name) {
      const confirmed = await this.generateModalInfos(name, 'finish')
      if (!confirmed) return
      this.$refs.table.paginationLoading = true
      await this.$store
        .dispatch('mutateHighlight', { id, action: 'finish' })
        .then(() => {
          this.$refs.table.waitAndGetData()
        })
        .catch((error) => {
          console.error(error)
          this.$snackbar(this.$t('campanhas.snackbars.errors.unschedule'))
        })
      this.$refs.table.paginationLoading = false
    },
    getHighlightDuration(item) {
      if (item.status === 'ACTIVE') {
        const { days: endCampaign } = DateTime.fromISO(item.finalDate).diffNow(['days']).toObject()
        switch (true) {
          case parseInt(endCampaign) === 0:
            return 'A campanha terminará hoje'
          default:
            return `A campanha terminará em ${parseInt(endCampaign)} dia(s)`
        }
      } else if (item.status === 'SCHEDULED') {
        const { days: initCampaign } = DateTime.fromISO(item.initialDate).diffNow(['days']).toObject()
        switch (true) {
          case parseInt(initCampaign) === 0:
            return 'A campanha iniciará amanhã'
          default:
            return `A campanha iniciará em ${parseInt(initCampaign)} dia(s)`
        }
      }
    },
    hasResults(status) {
      return ['ACTIVE', 'FINISHED'].includes(status)
    },
    getMessageDates(date, time = null) {
      let messageData = ''
      let dataFormater = ''
      if (time) {
        messageData = ''.concat(date, ' ', time)
        dataFormater = 'DD/MM/YYYY | HH:mm'
      } else {
        messageData = date
        dataFormater = 'DD/MM/YYYY'
      }
      return date ? DateTime.fromISO(messageData).toFormat(dataFormater) : '-'
    },
    showTooltip(status) {
      return status === 'ACTIVE' || status === 'SCHEDULED'
    },
    proposalDeadlineExpired(proposal_deadline) {
      return DateTime.now() > DateTime.fromISO(proposal_deadline).endOf('day')
    },
    getOfferTypes(types) {
      const offerTypes = []
      types.forEach((type) => offerTypes.push(getProductFormatTypeLabel(type)))
      return offerTypes
    },
    getOfferTypesTooltip(types) {
      return this.getOfferTypes(types).join(', ')
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.industry ? this.industryDefaultHeaders : this.defaultHeaders)
    },
    verifyHeaderProperty(property) {
      let exists = false
      const headers = this.computedHeaders
      headers.map((item) => {
        item.value === property && (exists = true)
      })
      return exists
    },
  },
}
</script>

<style lang="scss" scoped>
.card-title {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-bottom: 16px;
}

.min-width-345 {
  min-width: 345px;
}

.status-search-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.history-striped {
  tr:nth-child(even) {
    background-color: #f7f7f7;
  }
}

.text-align-right {
  text-align: right;
  margin-right: 12px;
}

.colorful-border {
  border-left: 3px solid;
  height: 100%;
}

tr td:not(:first-child) .colorful-border {
  border: none;
}

::v-deep td {
  padding: 0 !important;
}

.margin-left-btn {
  margin-left: 12px;
}

.primary-color {
  background-color: $marca-0;
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: $letterspacing-1;
}

.fix-td-position {
  padding: 0 16px;
  display: flex;
  align-items: center;
}

.modality-chip {
  width: 95px;
  text-align: center;
  display: flex;
  justify-content: center;
}

@media screen and (max-width: 1366px) {
  .link {
    font-size: 12px !important;
  }
}
</style>
